<script setup lang="ts">
/**
 * FitTabBar — Multi-tab bar above the fitting editor
 * Displays open fit tabs with close buttons + "new tab" button
 */
import { useFitStore } from '../../stores/fitStore';

const store = useFitStore();

function handleMiddleClick(e: MouseEvent, tabId: string): void {
  if (e.button === 1) {
    e.preventDefault();
    store.closeTab(tabId);
  }
}
</script>

<template>
  <div class="tab-bar" v-if="store.tabs.length > 0">
    <div class="tab-bar-scroll">
      <div
        v-for="tab in store.tabs"
        :key="tab.id"
        class="tab"
        :class="{ 'tab--active': store.activeTabId === tab.id }"
        @click="store.switchTab(tab.id)"
        @mousedown="handleMiddleClick($event, tab.id)"
      >
        <span class="tab-label" :title="tab.label">{{ tab.label }}</span>
        <button
          class="tab-close"
          title="Close tab"
          @click.stop="store.closeTab(tab.id)"
        >
          <svg width="10" height="10" viewBox="0 0 10 10">
            <line x1="2" y1="2" x2="8" y2="8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
            <line x1="8" y1="2" x2="2" y2="8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
          </svg>
        </button>
        <div v-if="tab.loading" class="tab-loading" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.tab-bar {
  display: flex;
  align-items: stretch;
  background: var(--bg-darker);
  border-bottom: 1px solid var(--border-color);
  flex-shrink: 0;
  min-height: 32px;
}

.tab-bar-scroll {
  display: flex;
  overflow-x: auto;
  flex: 1;
  scrollbar-width: none;
}

.tab-bar-scroll::-webkit-scrollbar {
  display: none;
}

.tab {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 10px;
  min-width: 80px;
  max-width: 180px;
  height: 32px;
  border-right: 1px solid var(--border-color);
  background: var(--bg-darker);
  color: var(--text-secondary);
  font-size: 0.78rem;
  cursor: pointer;
  transition: all var(--transition-fast);
  position: relative;
  flex-shrink: 0;
}

.tab:hover {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.tab--active {
  background: var(--bg-panel);
  color: var(--text-primary);
  border-bottom: 2px solid var(--accent-blue);
}

.tab-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.tab-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  border-radius: var(--radius-sm);
  opacity: 0;
  transition: all var(--transition-fast);
  flex-shrink: 0;
}

.tab:hover .tab-close {
  opacity: 1;
}

.tab-close:hover {
  background: rgba(244, 67, 54, 0.2);
  color: var(--accent-red);
}

.tab-loading {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: var(--accent-blue);
  animation: tab-loading-pulse 1s ease-in-out infinite;
}

@keyframes tab-loading-pulse {
  0%, 100% { opacity: 0.3; }
  50% { opacity: 1; }
}
</style>
